import { format, addDays, subDays, isSameDay } from 'date-fns';
import { cn, getRealWorldDate, isRealWorldToday } from '@/lib/utils';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDateVisibility } from '@/hooks/useDateVisibility';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { useMemo } from 'react';

interface DateSelectorProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
}

export function DateSelector({ selectedDate, onDateChange }: DateSelectorProps) {
  const { settings, isLoading } = useDateVisibility();
  
  const { minDate, maxDate, visibleDates } = useMemo(() => {
    const today = getRealWorldDate();
    const min = subDays(today, settings.days_before);
    const max = addDays(today, settings.days_after);
    
    // Show 5 dates centered around selectedDate, within bounds
    const dates: Date[] = [];
    for (let i = -2; i <= 2; i++) {
      const date = addDays(selectedDate, i);
      if (date >= min && date <= max) {
        dates.push(date);
      }
    }
    
    return { minDate: min, maxDate: max, visibleDates: dates };
  }, [selectedDate, settings]);

  const goToPreviousDay = () => {
    const newDate = subDays(selectedDate, 1);
    if (newDate >= minDate) {
      onDateChange(newDate);
    }
  };

  const goToNextDay = () => {
    const newDate = addDays(selectedDate, 1);
    if (newDate <= maxDate) {
      onDateChange(newDate);
    }
  };

  const canGoPrevious = selectedDate > minDate;
  const canGoNext = selectedDate < maxDate;

  // Only show skeleton on initial load when no settings available yet
  if (isLoading && !settings) {
    return (
      <div className="flex items-center justify-center gap-1.5 sm:gap-2 py-2 sm:py-3">
        <div className="flex gap-1.5 sm:gap-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="w-[48px] sm:w-[56px] h-[52px] sm:h-[64px] bg-muted rounded-lg sm:rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center gap-1 sm:gap-2 py-2 sm:py-4">
      {/* Previous button - compact on mobile */}
      <Button
        variant="ghost"
        size="icon"
        onClick={goToPreviousDay}
        disabled={!canGoPrevious}
        className="h-9 w-9 sm:h-11 sm:w-11 shrink-0 hover:bg-secondary rounded-lg sm:rounded-xl"
      >
        <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
      </Button>

      {/* Date buttons - Compact on mobile */}
      <div className="flex gap-1 sm:gap-2 overflow-x-auto scrollbar-hide px-0.5 -mx-0.5">
        {visibleDates.map((date, index) => {
          const isSelected = isSameDay(date, selectedDate);
          const today = isRealWorldToday(date);

          return (
            <button
              key={date.toISOString()}
              onClick={() => onDateChange(date)}
              className={cn(
                // Base styles - smaller on mobile
                'flex flex-col items-center justify-center px-2 sm:px-3 py-1.5 sm:py-2.5 rounded-lg sm:rounded-xl min-w-[44px] sm:min-w-[56px] min-h-[48px] sm:min-h-[64px]',
                // Transitions
                'transition-all duration-200 ease-out',
                // Touch feedback
                'active:scale-95',
                // Selected vs default states
                isSelected
                  ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/30'
                  : 'bg-card hover:bg-secondary text-foreground border border-border/60'
              )}
              style={{ animationDelay: `${index * 30}ms` }}
            >
              <span className={cn(
                "text-[9px] sm:text-[11px] uppercase font-bold leading-tight",
                isSelected ? "opacity-90" : "opacity-60"
              )}>
                {today ? 'Today' : format(date, 'EEE')}
              </span>
              <span className="text-lg sm:text-xl font-bold leading-tight mt-0.5">{format(date, 'd')}</span>
              <span className={cn(
                "text-[9px] sm:text-[11px] leading-tight mt-0.5 font-medium",
                isSelected ? "opacity-80" : "opacity-50"
              )}>
                {format(date, 'MMM')}
              </span>
            </button>
          );
        })}
      </div>

      {/* Next button - compact on mobile */}
      <Button
        variant="ghost"
        size="icon"
        onClick={goToNextDay}
        disabled={!canGoNext}
        className="h-9 w-9 sm:h-11 sm:w-11 shrink-0 hover:bg-secondary rounded-lg sm:rounded-xl"
      >
        <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
      </Button>

      {/* Calendar picker - compact on mobile */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="icon" className="h-9 w-9 sm:h-11 sm:w-11 ml-0.5 sm:ml-1 shrink-0 border-border/60 rounded-lg sm:rounded-xl">
            <Calendar className="h-4 w-4 sm:h-5 sm:w-5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <CalendarPicker
            mode="single"
            selected={selectedDate}
            onSelect={(date) => date && onDateChange(date)}
            disabled={(date) => date < minDate || date > maxDate}
            initialFocus
            className="p-3 pointer-events-auto"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
